import { useState, useEffect, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { 
  Home, Phone, Mail, MapPin, Clock, CheckCircle, Star, Menu, X, User, 
  LogOut, Calendar, DollarSign, Users, Settings, Sparkles, Shield, 
  ChevronRight, ArrowRight, Loader2, Building, Bed, Bath, Ruler, Plus, 
  Edit, Trash2, Save, TrendingUp, Package
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to ensure trailing slash for API calls
const apiUrl = (path) => {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `${API}/${cleanPath}${cleanPath.endsWith('/') ? '' : '/'}`;
};

// Configure axios
axios.defaults.withCredentials = true;

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setUser(response.data);
    return response.data;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (!user) return <Navigate to="/login" />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" />;
  
  return children;
};

// Navigation Component
const Navigation = () => {
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/");
    toast.success("Logged out successfully");
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src="https://www.jeromecleaning.com/hs-fs/hubfs/Jerome%20Cleaning%20Logo%20Design-4.png?width=990&height=990&name=Jerome%20Cleaning%20Logo%20Design-4.png" 
              alt="Jerome Cleaning" 
              className="h-10 w-10 rounded-full object-cover"
            />
            <span className="font-outfit font-semibold text-xl text-foreground">Jerome Cleaning</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className={`text-sm font-medium transition-colors ${isActive('/') ? 'text-primary' : 'text-slate-600 hover:text-primary'}`}>
              Home
            </Link>
            <Link to="/services" className={`text-sm font-medium transition-colors ${isActive('/services') ? 'text-primary' : 'text-slate-600 hover:text-primary'}`}>
              Services
            </Link>
            <Link to="/quote" className={`text-sm font-medium transition-colors ${isActive('/quote') ? 'text-primary' : 'text-slate-600 hover:text-primary'}`}>
              Get Quote
            </Link>
            <Link to="/contact" className={`text-sm font-medium transition-colors ${isActive('/contact') ? 'text-primary' : 'text-slate-600 hover:text-primary'}`}>
              Contact
            </Link>
            
            {user ? (
              <div className="flex items-center gap-4">
                <Link to={user.role === 'admin' ? '/admin' : '/dashboard'}>
                  <Button variant="outline" size="sm" data-testid="dashboard-btn">
                    <User className="h-4 w-4 mr-2" />
                    {user.role === 'admin' ? 'Admin' : 'Dashboard'}
                  </Button>
                </Link>
                <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <Button variant="ghost" size="sm" data-testid="login-btn">Log In</Button>
                </Link>
                <Link to="/book">
                  <Button size="sm" className="rounded-full bg-primary hover:bg-accent" data-testid="book-now-btn">
                    Book Now
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button 
            className="md:hidden p-2" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-slate-200 py-4 px-4">
          <div className="flex flex-col gap-4">
            <Link to="/" className="text-slate-600 hover:text-primary py-2" onClick={() => setMobileMenuOpen(false)}>Home</Link>
            <Link to="/services" className="text-slate-600 hover:text-primary py-2" onClick={() => setMobileMenuOpen(false)}>Services</Link>
            <Link to="/quote" className="text-slate-600 hover:text-primary py-2" onClick={() => setMobileMenuOpen(false)}>Get Quote</Link>
            <Link to="/contact" className="text-slate-600 hover:text-primary py-2" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
            {user ? (
              <>
                <Link to={user.role === 'admin' ? '/admin' : '/dashboard'} className="text-slate-600 hover:text-primary py-2" onClick={() => setMobileMenuOpen(false)}>
                  {user.role === 'admin' ? 'Admin Dashboard' : 'My Dashboard'}
                </Link>
                <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="text-left text-slate-600 hover:text-primary py-2">
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-slate-600 hover:text-primary py-2" onClick={() => setMobileMenuOpen(false)}>Log In</Link>
                <Link to="/book" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full rounded-full bg-primary">Book Now</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

// Footer Component
const Footer = () => (
  <footer className="bg-slate-900 text-white py-16" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img 
              src="https://www.jeromecleaning.com/hs-fs/hubfs/Jerome%20Cleaning%20Logo%20Design-4.png?width=990&height=990&name=Jerome%20Cleaning%20Logo%20Design-4.png" 
              alt="Jerome Cleaning" 
              className="h-12 w-12 rounded-full object-cover"
            />
            <span className="font-outfit font-semibold text-xl">Jerome Cleaning</span>
          </div>
          <p className="text-slate-400 text-sm leading-relaxed">
            Professional cleaning services you can trust. Serving Syracuse, NY and surrounding areas since 2003.
          </p>
        </div>
        
        <div>
          <h4 className="font-outfit font-semibold mb-4">Quick Links</h4>
          <div className="flex flex-col gap-2">
            <Link to="/services" className="text-slate-400 hover:text-white text-sm">Our Services</Link>
            <Link to="/quote" className="text-slate-400 hover:text-white text-sm">Get a Quote</Link>
            <Link to="/book" className="text-slate-400 hover:text-white text-sm">Book Now</Link>
            <Link to="/contact" className="text-slate-400 hover:text-white text-sm">Contact Us</Link>
          </div>
        </div>
        
        <div>
          <h4 className="font-outfit font-semibold mb-4">Services</h4>
          <div className="flex flex-col gap-2">
            <Link to="/book" className="text-slate-400 hover:text-white text-sm">Standard Cleaning</Link>
            <Link to="/book" className="text-slate-400 hover:text-white text-sm">Deep Cleaning</Link>
            <Link to="/book" className="text-slate-400 hover:text-white text-sm">Move-Out Cleaning</Link>
            <Link to="/book" className="text-slate-400 hover:text-white text-sm">Office Cleaning</Link>
          </div>
        </div>
        
        <div>
          <h4 className="font-outfit font-semibold mb-4">Contact Info</h4>
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <MapPin className="h-4 w-4" />
              <span>Syracuse, NY</span>
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Mail className="h-4 w-4" />
              <a href="mailto:clean@jeromecleaning.com" className="hover:text-white">clean@jeromecleaning.com</a>
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Phone className="h-4 w-4" />
              <span>(315) 308-1694</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-slate-400 text-sm text-center md:text-left">
          © {new Date().getFullYear()} Jerome Cleaning. All rights reserved. | "When you have a mess, don't stress — call the best."
        </p>
        <Link to="/privacy" className="text-slate-400 hover:text-white text-sm" data-testid="privacy-link">
          Privacy Policy
        </Link>
      </div>
    </div>
  </footer>
);

// Home Page — HubSpot landing page acts as our primary conversion surface.
// All lead capture (form submits, CTAs) flows through HubSpot CRM.
// Site navigation stays intact for visitors who want to browse further.
const HomePage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <div className="relative w-full" style={{ height: 'calc(100vh - 4rem)' }}>
        <iframe
          src="https://www.jeromecleaning.com/en-us/get-20-off-your-first-cleaning"
          title="Jerome Cleaning — Get 20% Off Your First Cleaning"
          className="absolute inset-0 w-full h-full border-0"
          loading="eager"
          allow="fullscreen; clipboard-write"
          data-testid="hubspot-landing-iframe"
        />
      </div>
    </div>
  );
};

// Services Page
const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const navigate = useNavigate();

  const fetchServices = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/services/`);
      setServices(response.data);
    } catch (error) {
      console.error("Error fetching services:", error);
    }
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const serviceImages = {
    "studio_standard": "https://images.unsplash.com/photo-1648475236583-2e25a6cbf3bd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxjbGVhbiUyMGJyaWdodCUyMGhvbWUlMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzU3OTcwMzR8MA&ixlib=rb-4.1.0&q=85",
    "studio_deep": "https://images.pexels.com/photos/34558066/pexels-photo-34558066.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "studio_moveout": "https://images.unsplash.com/photo-1692133220749-1c55bb918ad8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxjbGVhbiUyMGJyaWdodCUyMGhvbWUlMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzU3OTcwMzR8MA&ixlib=rb-4.1.0&q=85",
    "one_bedroom": "https://images.pexels.com/photos/8134781/pexels-photo-8134781.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "two_bedroom": "https://images.unsplash.com/photo-1648475236583-2e25a6cbf3bd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxjbGVhbiUyMGJyaWdodCUyMGhvbWUlMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzU3OTcwMzR8MA&ixlib=rb-4.1.0&q=85",
    "three_bedroom": "https://images.pexels.com/photos/34558066/pexels-photo-34558066.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "four_bedroom": "https://images.unsplash.com/photo-1692133220749-1c55bb918ad8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxjbGVhbiUyMGJyaWdodCUyMGhvbWUlMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzU3OTcwMzR8MA&ixlib=rb-4.1.0&q=85",
    "move_out": "https://images.pexels.com/photos/8134781/pexels-photo-8134781.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "senior": "https://images.unsplash.com/photo-1648475236583-2e25a6cbf3bd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxjbGVhbiUyMGJyaWdodCUyMGhvbWUlMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzU3OTcwMzR8MA&ixlib=rb-4.1.0&q=85",
    "refrigerator": "https://images.pexels.com/photos/34558066/pexels-photo-34558066.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "stove": "https://images.unsplash.com/photo-1692133220749-1c55bb918ad8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxjbGVhbiUyMGJyaWdodCUyMGhvbWUlMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzU3OTcwMzR8MA&ixlib=rb-4.1.0&q=85"
  };

  return (
    <div className="min-h-screen py-12" data-testid="services-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="font-outfit text-4xl md:text-5xl tracking-tight font-semibold text-foreground mb-4">
            Our Services
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Professional cleaning solutions with simple, transparent pricing.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => (
            <Card key={service.id} className="overflow-hidden hover:shadow-lg transition-all hover:-translate-y-1 border-slate-200" data-testid={`service-card-${service.id}`}>
              <div className="h-40 overflow-hidden">
                <img src={serviceImages[service.id] || serviceImages["one_bedroom"]} alt={service.name} className="w-full h-full object-cover" />
              </div>
              <CardHeader>
                <CardTitle className="font-outfit text-lg">{service.name}</CardTitle>
                <CardDescription className="text-sm">{service.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <span className="text-2xl font-bold text-primary">${service.price}</span>
                  </div>
                  <div className="flex items-center gap-1 text-slate-500">
                    <Clock className="h-4 w-4" />
                    <span className="text-sm">{service.duration}</span>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button className="w-full rounded-full" onClick={() => navigate('/book', { state: { serviceId: service.id } })}>
                  Book Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

// Quote Calculator Page - Now a simple service selector
const QuotePage = () => {
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchServices = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/services/`);
      setServices(response.data);
      if (response.data.length > 0) {
        setSelectedService(response.data[0]);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  return (
    <div className="min-h-screen py-12 bg-muted/30" data-testid="quote-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="font-outfit text-4xl md:text-5xl tracking-tight font-semibold text-foreground mb-4">
            Service Pricing
          </h1>
          <p className="text-slate-600">
            Simple, transparent pricing for all our cleaning services.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card data-testid="service-list">
              <CardHeader>
                <CardTitle className="font-outfit">Select a Service</CardTitle>
                <CardDescription>Click on a service to see details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {services.map((service) => (
                  <div 
                    key={service.id}
                    className={`p-4 rounded-lg cursor-pointer transition-all ${
                      selectedService?.id === service.id 
                        ? 'bg-primary text-white' 
                        : 'bg-slate-50 hover:bg-slate-100'
                    }`}
                    onClick={() => setSelectedService(service)}
                    data-testid={`service-option-${service.id}`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium">{service.name}</span>
                      <span className="font-bold">${service.price}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {selectedService && (
              <Card className="bg-primary text-white" data-testid="selected-service">
                <CardHeader>
                  <CardTitle className="font-outfit text-white">{selectedService.name}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center border-b border-white/20 pb-3">
                      <span className="text-white/70">Service Price:</span>
                      <span className="text-2xl font-bold">${selectedService.price.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-white/70">Deposit:</span>
                      <span className="text-xl">$50.00</span>
                    </div>
                    <div className="flex justify-between items-center border-t border-white/20 pt-2">
                      <span className="text-white/70">Subtotal:</span>
                      <span className="text-xl">${(selectedService.price + 50).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-white/70">NY Tax (8%):</span>
                      <span className="text-xl">${((selectedService.price + 50) * 0.08).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center border-t border-white/20 pt-3">
                      <span className="font-semibold text-lg">Total:</span>
                      <span className="text-3xl font-bold" data-testid="service-price">${((selectedService.price + 50) * 1.08).toFixed(2)}</span>
                    </div>
                  </div>

                  <p className="text-white/80 text-sm">{selectedService.description}</p>

                  <div className="flex items-center gap-2 pt-2 border-t border-white/20">
                    <Clock className="h-5 w-5" />
                    <span>Duration: {selectedService.duration}</span>
                  </div>

                  <Button 
                    variant="secondary" 
                    className="w-full rounded-full"
                    onClick={() => navigate('/book', { state: { serviceId: selectedService.id } })}
                    data-testid="book-service-btn"
                  >
                    Book This Service
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Contact Page
const ContactPage = () => {
  useEffect(() => {
    // Load HubSpot form script
    const script = document.createElement('script');
    script.src = '//js-na2.hsforms.net/forms/embed/v2.js';
    script.charset = 'utf-8';
    script.type = 'text/javascript';
    script.async = true;
    
    script.onload = () => {
      if (window.hbspt) {
        window.hbspt.forms.create({
          portalId: "243648268",
          formId: "bdd0f766-4d08-46b6-aa2c-cfced35cc265",
          region: "na2",
          target: "#hubspot-form-container"
        });
      }
    };
    
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="min-h-screen py-12" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="font-outfit text-4xl md:text-5xl tracking-tight font-semibold text-foreground mb-4">
            Contact Us
          </h1>
          <p className="text-slate-600">
            Have a question? We'd love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <Card data-testid="contact-form">
            <CardHeader>
              <CardTitle className="font-outfit">Send Us a Message</CardTitle>
              <CardDescription>Fill out the form below and we'll get back to you within one business day.</CardDescription>
            </CardHeader>
            <CardContent>
              {/* HubSpot Form Container */}
              <div id="hubspot-form-container" className="hubspot-form" data-testid="hubspot-contact-form"></div>
            </CardContent>
          </Card>

          <div className="space-y-8">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Location</h3>
                    <p className="text-slate-600">Syracuse, NY and surrounding areas</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Mail className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Email</h3>
                    <a href="mailto:clean@jeromecleaning.com" className="text-primary hover:underline">
                      clean@jeromecleaning.com
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Phone className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Phone</h3>
                    <p className="text-slate-600">(315) 308-1694</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Clock className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Business Hours</h3>
                    <p className="text-slate-600">Monday - Friday: 8am - 5pm</p>
                    <p className="text-slate-600">Saturday: 9am - 3pm</p>
                    <p className="text-slate-600">Sunday: Closed</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// Login Page
const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate(user.role === 'admin' ? '/admin' : '/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const userData = await login(email, password);
      toast.success("Welcome back!");
      navigate(userData.role === 'admin' ? '/admin' : '/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 bg-muted/30" data-testid="login-page">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="text-center">
          <img 
            src="https://www.jeromecleaning.com/hs-fs/hubfs/Jerome%20Cleaning%20Logo%20Design-4.png?width=990&height=990&name=Jerome%20Cleaning%20Logo%20Design-4.png" 
            alt="Jerome Cleaning" 
            className="h-16 w-16 mx-auto mb-4 rounded-full"
          />
          <CardTitle className="font-outfit text-2xl">Welcome Back</CardTitle>
          <CardDescription>Sign in to your account</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm" data-testid="login-error">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email" 
                type="email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input 
                id="password" 
                type="password" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>
            <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="login-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Sign In
            </Button>
          </form>
        </CardContent>
        <CardFooter className="justify-center">
          <p className="text-sm text-slate-600">
            Don't have an account?{" "}
            <Link to="/register" className="text-primary hover:underline" data-testid="register-link">
              Sign up
            </Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

// Register Page
const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    address: "",
    notification_preference: "email"
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { register, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.notification_preference !== "email" && !formData.phone) {
      setError("Phone number is required for SMS notifications");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await register(formData);
      toast.success("Account created successfully!");
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 bg-muted/30" data-testid="register-page">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="text-center">
          <img 
            src="https://www.jeromecleaning.com/hs-fs/hubfs/Jerome%20Cleaning%20Logo%20Design-4.png?width=990&height=990&name=Jerome%20Cleaning%20Logo%20Design-4.png" 
            alt="Jerome Cleaning" 
            className="h-16 w-16 mx-auto mb-4 rounded-full"
          />
          <CardTitle className="font-outfit text-2xl">Create Account</CardTitle>
          <CardDescription>Sign up to book cleaning services</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 text-red-600 rounded-lg text-sm" data-testid="register-error">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input 
                id="name" 
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                data-testid="register-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email" 
                type="email" 
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                data-testid="register-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input 
                id="password" 
                type="password" 
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
                data-testid="register-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number {formData.notification_preference !== "email" && <span className="text-red-500">*</span>}</Label>
              <Input 
                id="phone" 
                type="tel" 
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="(315) 555-0100"
                required={formData.notification_preference !== "email"}
                data-testid="register-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Notification Preference</Label>
              <Select 
                value={formData.notification_preference} 
                onValueChange={(value) => setFormData({...formData, notification_preference: value})}
              >
                <SelectTrigger data-testid="notification-preference-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email Only</SelectItem>
                  <SelectItem value="sms">Text Message (SMS) Only</SelectItem>
                  <SelectItem value="both">Both Email & Text</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">How would you like to receive booking confirmations and reminders?</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address (optional)</Label>
              <Input 
                id="address" 
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                data-testid="register-address-input"
              />
            </div>
            <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="register-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Create Account
            </Button>
          </form>
        </CardContent>
        <CardFooter className="justify-center">
          <p className="text-sm text-slate-600">
            Already have an account?{" "}
            <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

// Booking Page
const BookingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const prefilledServiceId = location.state?.serviceId;

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [bookingId, setBookingId] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [card, setCard] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [formData, setFormData] = useState({
    service_id: prefilledServiceId || "",
    address: "",
    city: "Syracuse",
    state: "NY",
    zip_code: "",
    scheduled_date: "",
    scheduled_time: "",
    special_instructions: ""
  });

  const fetchServices = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/services/`);
      setServices(response.data);
      if (prefilledServiceId) {
        const service = response.data.find(s => s.id === prefilledServiceId);
        setSelectedService(service);
        setFormData(prev => ({ ...prev, service_id: prefilledServiceId }));
      }
    } catch (error) {
      console.error("Error fetching services:", error);
    }
  }, [prefilledServiceId]);

  const fetchPaymentConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/payments/config`);
      setPaymentConfig(response.data);
    } catch (error) {
      console.error("Error fetching payment config:", error);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      toast.info("Please log in or create an account to book a service");
      navigate('/login', { state: { returnTo: '/book' } });
    } else {
      fetchServices();
      fetchPaymentConfig();
    }
  }, [user, navigate, fetchServices, fetchPaymentConfig]);

  const handleServiceSelect = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    setSelectedService(service);
    setFormData(prev => ({ ...prev, service_id: serviceId }));
  };

  const initSquareCard = useCallback(async () => {
    try {
      const payments = window.Square.payments(paymentConfig.application_id, 
        paymentConfig.environment === 'sandbox' ? 'sandbox' : 'production');
      const cardInstance = await payments.card();
      await cardInstance.attach('#card-container');
      setCard(cardInstance);
    } catch (error) {
      console.error('Error initializing Square:', error);
      toast.error('Error loading payment form');
    }
  }, [paymentConfig]);

  const initializeSquarePayment = useCallback(async () => {
    if (!window.Square) {
      const script = document.createElement('script');
      script.src = paymentConfig.environment === 'sandbox' 
        ? 'https://sandbox.web.squarecdn.com/v1/square.js'
        : 'https://web.squarecdn.com/v1/square.js';
      script.onload = () => initSquareCard();
      document.body.appendChild(script);
    } else {
      initSquareCard();
    }
  }, [paymentConfig, initSquareCard]);

  // Initialize Square payment form when on step 4
  useEffect(() => {
    if (step === 4 && paymentConfig && !card) {
      initializeSquarePayment();
    }
  }, [step, paymentConfig, card, initializeSquarePayment]);

  const handleCreateBooking = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/bookings/`, formData);
      setBookingId(response.data.id);
      setStep(4);
    } catch (error) {
      toast.error("Error creating booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePayDeposit = async () => {
    if (!card) {
      toast.error('Payment form not ready. Please wait.');
      return;
    }
    setPaymentLoading(true);
    try {
      const result = await card.tokenize();
      if (result.status === 'OK') {
        const response = await axios.post(`${API}/payments/process-deposit`, {
          source_id: result.token,
          booking_id: bookingId
        });
        toast.success(response.data.message);
        navigate('/dashboard');
      } else {
        toast.error('Payment failed: ' + (result.errors?.[0]?.message || 'Unknown error'));
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Payment failed. Please try again.';
      toast.error(errorMsg);
    } finally {
      setPaymentLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen py-12 bg-muted/30" data-testid="booking-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="font-outfit text-4xl md:text-5xl tracking-tight font-semibold text-foreground mb-4">
            Book Your Cleaning
          </h1>
          <p className="text-slate-600">
            Complete your booking in just a few steps.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                step >= s ? 'bg-primary text-white' : 'bg-slate-200 text-slate-600'
              }`}>
                {s}
              </div>
              {s < 4 && <div className={`w-16 h-1 ${step > s ? 'bg-primary' : 'bg-slate-200'}`} />}
            </div>
          ))}
        </div>

        <form onSubmit={handleCreateBooking}>
          {step === 1 && (
            <Card data-testid="booking-step-1">
              <CardHeader>
                <CardTitle className="font-outfit">Select Service</CardTitle>
                <CardDescription>Choose the cleaning service you need</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {services.map((service) => (
                    <div 
                      key={service.id}
                      className={`p-4 rounded-lg cursor-pointer border-2 transition-all ${
                        formData.service_id === service.id 
                          ? 'border-primary bg-primary/5' 
                          : 'border-slate-200 hover:border-primary/50'
                      }`}
                      onClick={() => handleServiceSelect(service.id)}
                      data-testid={`booking-service-${service.id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium">{service.name}</div>
                          <div className="text-sm text-slate-500">{service.duration}</div>
                        </div>
                        <div className="text-xl font-bold text-primary">${service.price}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {selectedService && (
                  <Card className="bg-secondary mt-4">
                    <CardContent className="pt-6">
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-sm text-slate-600">Selected Service</div>
                          <div className="text-xl font-bold">{selectedService.name}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-3xl font-bold text-primary">${selectedService.price}</div>
                          <div className="text-sm text-slate-500">{selectedService.duration}</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </CardContent>
              <CardFooter>
                <Button 
                  type="button" 
                  className="w-full rounded-full" 
                  onClick={() => setStep(2)} 
                  disabled={!formData.service_id}
                  data-testid="booking-next-btn"
                >
                  Continue
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </CardFooter>
            </Card>
          )}

          {step === 2 && (
            <Card data-testid="booking-step-2">
              <CardHeader>
                <CardTitle className="font-outfit">Address & Schedule</CardTitle>
                <CardDescription>Where and when should we come?</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="address">Street Address</Label>
                  <Input 
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="123 Main Street"
                    required
                    data-testid="booking-address-input"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input 
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      required
                      data-testid="booking-city-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State</Label>
                    <Input 
                      id="state"
                      value={formData.state}
                      onChange={(e) => setFormData({...formData, state: e.target.value})}
                      required
                      data-testid="booking-state-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zip_code">ZIP Code</Label>
                    <Input 
                      id="zip_code"
                      value={formData.zip_code}
                      onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                      required
                      data-testid="booking-zip-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="scheduled_date">Preferred Date</Label>
                    <Input 
                      id="scheduled_date"
                      type="date"
                      value={formData.scheduled_date}
                      onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                      min={new Date().toISOString().split('T')[0]}
                      required
                      data-testid="booking-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="scheduled_time">Preferred Time</Label>
                    <Select 
                      value={formData.scheduled_time} 
                      onValueChange={(value) => setFormData({...formData, scheduled_time: value})}
                    >
                      <SelectTrigger data-testid="booking-time-select">
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="08:00">8:00 AM</SelectItem>
                        <SelectItem value="09:00">9:00 AM</SelectItem>
                        <SelectItem value="10:00">10:00 AM</SelectItem>
                        <SelectItem value="11:00">11:00 AM</SelectItem>
                        <SelectItem value="12:00">12:00 PM</SelectItem>
                        <SelectItem value="13:00">1:00 PM</SelectItem>
                        <SelectItem value="14:00">2:00 PM</SelectItem>
                        <SelectItem value="15:00">3:00 PM</SelectItem>
                        <SelectItem value="16:00">4:00 PM</SelectItem>
                        <SelectItem value="17:00">5:00 PM</SelectItem>
                        <SelectItem value="18:00">6:00 PM</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="special_instructions">Special Instructions (optional)</Label>
                  <Textarea 
                    id="special_instructions"
                    value={formData.special_instructions}
                    onChange={(e) => setFormData({...formData, special_instructions: e.target.value})}
                    placeholder="Any special requests or instructions..."
                    rows={3}
                    data-testid="booking-instructions-input"
                  />
                </div>
              </CardContent>
              <CardFooter className="flex gap-4">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setStep(1)}>
                  Back
                </Button>
                <Button type="button" className="flex-1 rounded-full" onClick={() => setStep(3)} data-testid="booking-continue-btn">
                  Continue
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </CardFooter>
            </Card>
          )}

          {step === 3 && (
            <Card data-testid="booking-step-3">
              <CardHeader>
                <CardTitle className="font-outfit">Review & Continue to Payment</CardTitle>
                <CardDescription>Please review your booking details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium mb-3">Service Details</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Service:</span>
                        <span className="font-medium">{selectedService?.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Duration:</span>
                        <span>{selectedService?.duration}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-3">Schedule & Location</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-600">Date:</span>
                        <span>{formData.scheduled_date}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600">Time:</span>
                        <span>{formData.scheduled_time}</span>
                      </div>
                      <div>
                        <span className="text-slate-600">Address:</span>
                        <div className="font-medium">{formData.address}</div>
                        <div>{formData.city}, {formData.state} {formData.zip_code}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedService && (
                  <Card className="bg-primary text-white">
                    <CardContent className="pt-6">
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-white/70">Service Price:</span>
                          <span className="text-xl">${selectedService.price.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-white/70">Deposit:</span>
                          <span className="text-xl">$50.00</span>
                        </div>
                        <div className="flex justify-between items-center border-t border-white/20 pt-2">
                          <span className="text-white/70">Subtotal:</span>
                          <span className="text-xl">${(selectedService.price + 50).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-white/70">NY Tax (8%):</span>
                          <span className="text-xl">${((selectedService.price + 50) * 0.08).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center border-t border-white/20 pt-3">
                          <span className="font-semibold">Total Due:</span>
                          <span className="text-2xl font-bold">${((selectedService.price + 50) * 1.08).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm pt-2">
                          <span className="text-white/70">Deposit Required Now:</span>
                          <span className="font-medium">$50.00</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-white/70">Balance Due After Service:</span>
                          <span className="font-medium">${(((selectedService.price + 50) * 1.08) - 50).toFixed(2)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> A $50 deposit is required to confirm your booking. 
                    The total includes an 8% NY sales tax. The remaining balance will be due after your service is completed.
                  </p>
                </div>
              </CardContent>
              <CardFooter className="flex gap-4">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setStep(2)}>
                  Back
                </Button>
                <Button type="submit" className="flex-1 rounded-full" disabled={loading} data-testid="booking-to-payment-btn">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Continue to Payment
                </Button>
              </CardFooter>
            </Card>
          )}

          {step === 4 && (
            <Card data-testid="booking-step-4">
              <CardHeader>
                <CardTitle className="font-outfit">Pay $50 Deposit</CardTitle>
                <CardDescription>Enter your card details to confirm your booking</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200 mb-4">
                  <div className="flex justify-between items-center">
                    <span className="text-green-800 font-medium">Deposit Amount:</span>
                    <span className="text-2xl font-bold text-green-800">$50.00</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Card Details</Label>
                  <div 
                    id="card-container" 
                    className="min-h-[50px] p-3 border rounded-lg bg-white"
                  ></div>
                </div>

                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 text-sm text-slate-600">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="h-4 w-4 text-primary" />
                    <span className="font-medium">Secure Payment</span>
                  </div>
                  <p>Your payment is securely processed by Square. We never store your card details.</p>
                </div>
              </CardContent>
              <CardFooter className="flex gap-4">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setStep(3)}>
                  Back
                </Button>
                <Button 
                  type="button" 
                  className="flex-1 rounded-full bg-green-600 hover:bg-green-700" 
                  onClick={handlePayDeposit}
                  disabled={paymentLoading || !card}
                  data-testid="pay-deposit-btn"
                >
                  {paymentLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Pay $50 Deposit
                </Button>
              </CardFooter>
            </Card>
          )}
        </form>
      </div>
    </div>
  );
};

// Customer Dashboard
const CustomerDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBookings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bookings/`);
      setBookings(response.data);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const cancelBooking = async (bookingId) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/cancel`);
      toast.success("Booking cancelled");
      fetchBookings();
    } catch (error) {
      toast.error("Error cancelling booking");
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: "bg-yellow-100 text-yellow-800",
      confirmed: "bg-blue-100 text-blue-800",
      completed: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800"
    };
    return <Badge className={variants[status] || variants.pending}>{status}</Badge>;
  };

  return (
    <div className="min-h-screen bg-muted/30 py-8" data-testid="customer-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-outfit text-3xl font-semibold text-foreground">Welcome, {user?.name}</h1>
            <p className="text-slate-600">Manage your cleaning appointments</p>
          </div>
          <Button onClick={() => navigate('/book')} className="rounded-full" data-testid="new-booking-btn">
            <Calendar className="h-4 w-4 mr-2" />
            New Booking
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Calendar className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{bookings.filter(b => b.status === 'pending' || b.status === 'confirmed').length}</div>
                  <div className="text-sm text-slate-600">Upcoming</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{bookings.filter(b => b.status === 'completed').length}</div>
                  <div className="text-sm text-slate-600">Completed</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <DollarSign className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <div className="text-2xl font-bold">
                    ${bookings.filter(b => b.status === 'completed').reduce((sum, b) => sum + b.total_price, 0).toFixed(0)}
                  </div>
                  <div className="text-sm text-slate-600">Total Spent</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="font-outfit">Your Bookings</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : bookings.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <h3 className="font-medium mb-2">No bookings yet</h3>
                <p className="text-slate-600 mb-4">Book your first cleaning service today!</p>
                <Button onClick={() => navigate('/book')} className="rounded-full">
                  Book Now
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Service</TableHead>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.map((booking) => (
                    <TableRow key={booking.id} data-testid={`booking-row-${booking.id}`}>
                      <TableCell className="font-medium">
                        {booking.service_name}
                      </TableCell>
                      <TableCell>
                        {booking.scheduled_date} at {booking.scheduled_time}
                      </TableCell>
                      <TableCell>
                        {booking.address}, {booking.city}
                      </TableCell>
                      <TableCell>${booking.total_price.toFixed(2)}</TableCell>
                      <TableCell>{getStatusBadge(booking.status)}</TableCell>
                      <TableCell>
                        {(booking.status === 'pending' || booking.status === 'confirmed') && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-600 hover:text-red-700"
                            onClick={() => cancelBooking(booking.id)}
                            data-testid={`cancel-booking-${booking.id}`}
                          >
                            Cancel
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editingBooking, setEditingBooking] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editingPricing, setEditingPricing] = useState(false);
  const [showAddAppointment, setShowAddAppointment] = useState(false);
  const [newAppointment, setNewAppointment] = useState({
    customer_id: "",
    service_id: "",
    scheduled_date: "",
    scheduled_time: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    status: "confirmed",
    payment_status: "pending",
    admin_notes: ""
  });

  const fetchServices = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/services/`);
      setServices(response.data);
    } catch (error) {
      console.error("Error fetching services:", error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, bookingsRes, customersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/bookings${statusFilter && statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`),
        axios.get(`${API}/admin/customers`)
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      console.error("Error fetching admin data:", error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchData();
    fetchServices();
  }, [fetchData, fetchServices]);

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/status`, { status: newStatus });
      toast.success(`Booking ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error("Error updating booking");
    }
  };

  const updateBooking = async (bookingId, data) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}`, data);
      toast.success("Booking updated successfully");
      setEditingBooking(null);
      fetchData();
    } catch (error) {
      toast.error("Error updating booking");
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to delete this booking?")) return;
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`);
      toast.success("Booking deleted");
      fetchData();
    } catch (error) {
      toast.error("Error deleting booking");
    }
  };

  const updateCustomer = async (customerId, data) => {
    try {
      await axios.put(`${API}/admin/customers/${customerId}`, data);
      toast.success("Customer updated successfully");
      setEditingCustomer(null);
      fetchData();
    } catch (error) {
      toast.error("Error updating customer");
    }
  };

  const deleteCustomer = async (customerId) => {
    if (!window.confirm("Are you sure you want to delete this customer and all their bookings?")) return;
    try {
      await axios.delete(`${API}/admin/customers/${customerId}`);
      toast.success("Customer deleted");
      fetchData();
    } catch (error) {
      toast.error("Error deleting customer");
    }
  };

  const createAppointment = async () => {
    try {
      if (!newAppointment.customer_id || !newAppointment.service_id || !newAppointment.scheduled_date || !newAppointment.scheduled_time || !newAppointment.address) {
        toast.error("Please fill in all required fields");
        return;
      }

      await axios.post(`${API}/admin/bookings`, newAppointment);
      toast.success("Appointment created successfully!");
      setShowAddAppointment(false);
      setNewAppointment({
        customer_id: "",
        service_id: "",
        scheduled_date: "",
        scheduled_time: "",
        address: "",
        city: "",
        state: "",
        zip_code: "",
        status: "confirmed",
        payment_status: "pending",
        admin_notes: ""
      });
      fetchData();
    } catch (error) {
      toast.error("Error creating appointment");
      console.error(error);
    }
  };

  const updatePricing = async (newPricing) => {
    try {
      await axios.put(`${API}/admin/pricing`, newPricing);
      toast.success("Pricing updated successfully");
      setEditingPricing(false);
      fetchData();
    } catch (error) {
      toast.error("Error updating pricing");
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: "bg-yellow-100 text-yellow-800",
      confirmed: "bg-blue-100 text-blue-800",
      completed: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800"
    };
    return <Badge className={variants[status] || variants.pending}>{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-outfit text-3xl font-semibold text-foreground">Admin Dashboard</h1>
          <p className="text-slate-600">Welcome back, {user?.name}</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers">Customers</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="tab-pricing">Pricing</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <Calendar className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{stats?.total_bookings || 0}</div>
                      <div className="text-sm text-slate-600">Total Bookings</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-yellow-100 rounded-lg">
                      <Clock className="h-6 w-6 text-yellow-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{stats?.pending_bookings || 0}</div>
                      <div className="text-sm text-slate-600">Pending</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-green-100 rounded-lg">
                      <Users className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{stats?.total_customers || 0}</div>
                      <div className="text-sm text-slate-600">Customers</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-primary/10 rounded-lg">
                      <DollarSign className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">${stats?.total_revenue?.toFixed(0) || 0}</div>
                      <div className="text-sm text-slate-600">Revenue</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="font-outfit">Recent Bookings</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bookings.slice(0, 5).map((booking) => (
                      <TableRow key={booking.id}>
                        <TableCell>
                          <div className="font-medium">{booking.user_name}</div>
                          <div className="text-sm text-slate-500">{booking.user_email}</div>
                        </TableCell>
                        <TableCell>
                          {booking.service_type === 'standard' ? 'Standard' :
                           booking.service_type === 'deep_clean' ? 'Deep Clean' : 'Move-Out'}
                        </TableCell>
                        <TableCell>{booking.scheduled_date}</TableCell>
                        <TableCell>${booking.total_price.toFixed(2)}</TableCell>
                        <TableCell>{getStatusBadge(booking.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bookings">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-outfit">All Bookings</CardTitle>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => setShowAddAppointment(true)}
                    className="bg-primary hover:bg-primary/90"
                    data-testid="add-appointment-button"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Appointment
                  </Button>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-40" data-testid="status-filter">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Date & Time</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bookings.map((booking) => (
                      <TableRow key={booking.id} data-testid={`admin-booking-${booking.id}`}>
                        <TableCell>
                          <div className="font-medium">{booking.user_name}</div>
                          <div className="text-sm text-slate-500">{booking.user_email}</div>
                        </TableCell>
                        <TableCell>
                          {booking.service_type === 'standard' ? 'Standard' :
                           booking.service_type === 'deep_clean' ? 'Deep Clean' : 'Move-Out'}
                        </TableCell>
                        <TableCell>
                          {booking.scheduled_date} at {booking.scheduled_time}
                        </TableCell>
                        <TableCell>
                          <div>{booking.address}</div>
                          <div className="text-sm text-slate-500">{booking.city}, {booking.state}</div>
                        </TableCell>
                        <TableCell>${booking.total_price.toFixed(2)}</TableCell>
                        <TableCell>{getStatusBadge(booking.status)}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Select 
                              value={booking.status}
                              onValueChange={(value) => updateBookingStatus(booking.id, value)}
                            >
                              <SelectTrigger className="w-28" data-testid={`status-select-${booking.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="confirmed">Confirmed</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button variant="outline" size="sm" onClick={() => setEditingBooking(booking)}>
                                  Edit
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-md">
                                <DialogHeader>
                                  <DialogTitle>Edit Booking</DialogTitle>
                                  <DialogDescription>Update booking details</DialogDescription>
                                </DialogHeader>
                                {editingBooking && editingBooking.id === booking.id && (
                                  <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                      <div className="space-y-2">
                                        <Label>Date</Label>
                                        <Input 
                                          type="date"
                                          value={editingBooking.scheduled_date}
                                          onChange={(e) => setEditingBooking({...editingBooking, scheduled_date: e.target.value})}
                                        />
                                      </div>
                                      <div className="space-y-2">
                                        <Label>Time</Label>
                                        <Input 
                                          value={editingBooking.scheduled_time}
                                          onChange={(e) => setEditingBooking({...editingBooking, scheduled_time: e.target.value})}
                                        />
                                      </div>
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Address</Label>
                                      <Input 
                                        value={editingBooking.address}
                                        onChange={(e) => setEditingBooking({...editingBooking, address: e.target.value})}
                                      />
                                    </div>
                                    <div className="grid grid-cols-3 gap-2">
                                      <div className="space-y-2">
                                        <Label>City</Label>
                                        <Input 
                                          value={editingBooking.city}
                                          onChange={(e) => setEditingBooking({...editingBooking, city: e.target.value})}
                                        />
                                      </div>
                                      <div className="space-y-2">
                                        <Label>State</Label>
                                        <Input 
                                          value={editingBooking.state}
                                          onChange={(e) => setEditingBooking({...editingBooking, state: e.target.value})}
                                        />
                                      </div>
                                      <div className="space-y-2">
                                        <Label>ZIP</Label>
                                        <Input 
                                          value={editingBooking.zip_code}
                                          onChange={(e) => setEditingBooking({...editingBooking, zip_code: e.target.value})}
                                        />
                                      </div>
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Special Instructions</Label>
                                      <Textarea 
                                        value={editingBooking.special_instructions || ''}
                                        onChange={(e) => setEditingBooking({...editingBooking, special_instructions: e.target.value})}
                                      />
                                    </div>
                                    <Button 
                                      className="w-full" 
                                      onClick={() => updateBooking(editingBooking.id, editingBooking)}
                                    >
                                      Save Changes
                                    </Button>
                                  </div>
                                )}
                              </DialogContent>
                            </Dialog>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-600 hover:text-red-700"
                              onClick={() => deleteBooking(booking.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="customers">
            <Card>
              <CardHeader>
                <CardTitle className="font-outfit">All Customers</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Bookings</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customers.map((customer) => (
                      <TableRow key={customer.id} data-testid={`customer-row-${customer.id}`}>
                        <TableCell className="font-medium">{customer.name}</TableCell>
                        <TableCell>{customer.email}</TableCell>
                        <TableCell>{customer.phone || '-'}</TableCell>
                        <TableCell>{customer.booking_count}</TableCell>
                        <TableCell>
                          {new Date(customer.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button variant="outline" size="sm" onClick={() => setEditingCustomer(customer)}>
                                  Edit
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Edit Customer</DialogTitle>
                                  <DialogDescription>Update customer information</DialogDescription>
                                </DialogHeader>
                                {editingCustomer && editingCustomer.id === customer.id && (
                                  <div className="space-y-4">
                                    <div className="space-y-2">
                                      <Label>Name</Label>
                                      <Input 
                                        value={editingCustomer.name}
                                        onChange={(e) => setEditingCustomer({...editingCustomer, name: e.target.value})}
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Phone</Label>
                                      <Input 
                                        value={editingCustomer.phone || ''}
                                        onChange={(e) => setEditingCustomer({...editingCustomer, phone: e.target.value})}
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <Label>Address</Label>
                                      <Input 
                                        value={editingCustomer.address || ''}
                                        onChange={(e) => setEditingCustomer({...editingCustomer, address: e.target.value})}
                                      />
                                    </div>
                                    <Button 
                                      className="w-full" 
                                      onClick={() => updateCustomer(editingCustomer.id, editingCustomer)}
                                    >
                                      Save Changes
                                    </Button>
                                  </div>
                                )}
                              </DialogContent>
                            </Dialog>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-600 hover:text-red-700"
                              onClick={() => deleteCustomer(customer.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pricing">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-outfit">Service Pricing</CardTitle>
                <p className="text-sm text-slate-500">Contact support to modify pricing</p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { name: "Studio Apartment Standard", price: 135, duration: "1 hr 30 min" },
                    { name: "Studio Apartment Deep Clean", price: 175, duration: "4 hr" },
                    { name: "Studio Apartment Move-In/Out", price: 195, duration: "2 hr 30 min" },
                    { name: "One Bedroom", price: 165, duration: "2 hr" },
                    { name: "Two Bedroom", price: 265, duration: "3 hr" },
                    { name: "Three Bedroom", price: 275, duration: "3 hr" },
                    { name: "Four Bedroom", price: 365, duration: "3 hr 30 min" },
                    { name: "Move-Out Cleaning", price: 325, duration: "3 hr 45 min" },
                    { name: "Senior Cleaning", price: 80, duration: "4 hr min" },
                    { name: "Refrigerator Cleaning", price: 50, duration: "1 hr" },
                    { name: "Stove Cleaning", price: 60, duration: "1 hr" }
                  ].map((service) => (
                    <Card key={service.name} className="border">
                      <CardContent className="pt-4">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-medium text-sm">{service.name}</h4>
                          <span className="text-xl font-bold text-primary">${service.price}</span>
                        </div>
                        <div className="flex items-center gap-1 text-slate-500 text-sm">
                          <Clock className="h-3 w-3" />
                          <span>{service.duration}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>Need to update pricing?</strong> Just let me know what changes you need and I'll update them for you right away.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Add Appointment Modal */}
        {showAddAppointment && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-appointment-title"
            data-testid="add-appointment-modal"
          >
            <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <CardTitle className="font-outfit flex items-center justify-between">
                  <span id="add-appointment-title">Create New Appointment</span>
                  <button onClick={() => setShowAddAppointment(false)} className="text-gray-400 hover:text-gray-600" data-testid="close-add-appointment-modal" aria-label="Close">
                    <X className="h-6 w-6" />
                  </button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Customer *</label>
                    <select
                      value={newAppointment.customer_id}
                      onChange={(e) => setNewAppointment({...newAppointment, customer_id: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                      required
                    >
                      <option value="">Select a customer</option>
                      {customers.map((customer) => (
                        <option key={customer.id} value={customer.id}>
                          {customer.email} - {customer.name || 'No name'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Service *</label>
                    <select
                      value={newAppointment.service_id}
                      onChange={(e) => setNewAppointment({...newAppointment, service_id: e.target.value})}
                      className="w-full p-2 border rounded-lg"
                      required
                    >
                      <option value="">Select a service</option>
                      {services.map((service) => (
                        <option key={service.id} value={service.id}>
                          {service.name} - ${service.price}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Date *</label>
                      <input
                        type="date"
                        value={newAppointment.scheduled_date}
                        onChange={(e) => setNewAppointment({...newAppointment, scheduled_date: e.target.value})}
                        className="w-full p-2 border rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Time *</label>
                      <input
                        type="time"
                        value={newAppointment.scheduled_time}
                        onChange={(e) => setNewAppointment({...newAppointment, scheduled_time: e.target.value})}
                        className="w-full p-2 border rounded-lg"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Address *</label>
                    <input
                      type="text"
                      value={newAppointment.address}
                      onChange={(e) => setNewAppointment({...newAppointment, address: e.target.value})}
                      placeholder="123 Main St"
                      className="w-full p-2 border rounded-lg"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">City</label>
                      <input
                        type="text"
                        value={newAppointment.city}
                        onChange={(e) => setNewAppointment({...newAppointment, city: e.target.value})}
                        placeholder="Syracuse"
                        className="w-full p-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">State</label>
                      <input
                        type="text"
                        value={newAppointment.state}
                        onChange={(e) => setNewAppointment({...newAppointment, state: e.target.value})}
                        placeholder="NY"
                        maxLength="2"
                        className="w-full p-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">ZIP Code</label>
                      <input
                        type="text"
                        value={newAppointment.zip_code}
                        onChange={(e) => setNewAppointment({...newAppointment, zip_code: e.target.value})}
                        placeholder="13202"
                        className="w-full p-2 border rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Status</label>
                      <select
                        value={newAppointment.status}
                        onChange={(e) => setNewAppointment({...newAppointment, status: e.target.value})}
                        className="w-full p-2 border rounded-lg"
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Payment Status</label>
                      <select
                        value={newAppointment.payment_status}
                        onChange={(e) => setNewAppointment({...newAppointment, payment_status: e.target.value})}
                        className="w-full p-2 border rounded-lg"
                      >
                        <option value="pending">Pending</option>
                        <option value="paid">Paid</option>
                        <option value="cash">Cash/Check</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Admin Notes</label>
                    <textarea
                      value={newAppointment.admin_notes}
                      onChange={(e) => setNewAppointment({...newAppointment, admin_notes: e.target.value})}
                      placeholder="Customer called in, paid cash..."
                      rows="3"
                      className="w-full p-2 border rounded-lg"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button onClick={createAppointment} className="flex-1 bg-primary hover:bg-primary/90">
                      Create Appointment
                    </Button>
                    <Button onClick={() => setShowAddAppointment(false)} variant="outline" className="flex-1">
                      Cancel
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

// Special Offer Popup Component
const SpecialOfferPopup = () => {
  const { user } = useAuth();
  const [showPopup, setShowPopup] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Do not show popup to logged-in admins or on admin/dashboard pages
    if (user?.role === 'admin') return;
    if (location.pathname.startsWith('/admin') || location.pathname.startsWith('/dashboard')) return;

    // Show special offer popup after 3 seconds (once per session)
    const popupTimer = setTimeout(() => {
      const popupShown = sessionStorage.getItem('specialOfferShown');
      if (!popupShown) {
        setShowPopup(true);
        sessionStorage.setItem('specialOfferShown', 'true');
      }
    }, 3000);

    return () => clearTimeout(popupTimer);
  }, [user, location.pathname]);

  const handleBookNow = () => {
    setShowPopup(false);
    navigate('/book');
  };

  if (!showPopup) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{zIndex: 9999}}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full relative">
        <button
          onClick={() => setShowPopup(false)}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <X className="h-6 w-6" />
        </button>
        
        <div className="p-8 text-center">
          <div className="mb-4">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 rounded-full mb-4">
              <Sparkles className="w-10 h-10 text-primary" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Special Offer!
            </h2>
            <div className="text-5xl font-bold text-primary mb-2">
              20% OFF
            </div>
            <p className="text-xl text-gray-600 mb-6">
              Your First Cleaning
            </p>
            <p className="text-gray-500 text-sm mb-6">
              New customers only. Book your first cleaning service and save 20%!
            </p>
          </div>
          
          <div className="space-y-3">
            <button
              onClick={handleBookNow}
              className="block w-full bg-primary hover:bg-primary/90 text-white font-semibold py-3 px-6 rounded-full transition-colors"
            >
              Book Now & Save 20%
            </button>
            <button
              onClick={() => setShowPopup(false)}
              className="block w-full text-gray-500 hover:text-gray-700 text-sm transition-colors"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Analytics — fires a GA4 page_view on every React Router navigation
const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location]);

  return null;
};

// Privacy Policy Page
const PrivacyPolicyPage = () => {
  const lastUpdated = "April 2026";
  return (
    <div className="min-h-screen py-12 bg-muted/30" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="font-outfit text-4xl md:text-5xl tracking-tight font-semibold text-foreground mb-3">
            Privacy Policy
          </h1>
          <p className="text-slate-500 text-sm">Last updated: {lastUpdated}</p>
        </div>

        <Card>
          <CardContent className="pt-6 space-y-6 text-slate-700 leading-relaxed">
            <section>
              <p>
                Jerome Cleaning ("we," "us," or "our") respects your privacy. This Privacy Policy explains
                how we collect, use, and protect the information you provide when you visit jeromecleaning.com,
                request a quote, book a service, contact us, or pay a deposit.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-foreground mb-2">1. Information We Collect</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Contact information:</strong> name, email, phone number, and service address you submit through our contact, quote, registration, or booking forms.</li>
                <li><strong>Booking details:</strong> service type, preferred date and time, and any special instructions.</li>
                <li><strong>Payment information:</strong> processed securely by Square. We do not store your full card number on our servers.</li>
                <li><strong>Account credentials:</strong> if you create an account, we store your email and a securely hashed password.</li>
                <li><strong>Website usage data:</strong> pages visited, browser type, IP-derived approximate location, and similar analytics collected via Google Analytics.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-foreground mb-2">2. How We Use Your Information</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>Schedule and deliver the cleaning services you request.</li>
                <li>Process deposits and payments through Square.</li>
                <li>Send booking confirmations, reminders, and follow-up communications by email (and SMS, if you have opted in).</li>
                <li>Respond to your inquiries submitted through our contact form.</li>
                <li>Improve our website, services, and customer experience.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-foreground mb-2">3. Third-Party Services</h2>
              <p className="mb-2">We use trusted third-party providers to operate our website and business. Each has its own privacy policy:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Square</strong> — payment processing.</li>
                <li><strong>HubSpot</strong> — customer relationship management and contact form processing.</li>
                <li><strong>IONOS</strong> — email delivery (SMTP) for booking and contact notifications.</li>
                <li><strong>Google Analytics</strong> — anonymous website usage analytics.</li>
                <li><strong>Newo.ai</strong> — on-site chat widget.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-foreground mb-2">4. Cookies and Tracking</h2>
              <p>
                Our website uses cookies and similar technologies to keep you signed in, remember your preferences,
                and measure site performance. You can disable cookies in your browser settings, although some
                features (such as the booking flow) may not work properly without them.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-foreground mb-2">5. How We Protect Your Information</h2>
              <p>
                We use industry-standard security measures, including encrypted connections (HTTPS), secure
                password hashing, and PCI-compliant payment processing through Square. No method of transmission
                over the internet is 100% secure, but we work hard to protect your data.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-foreground mb-2">6. Sharing Your Information</h2>
              <p>
                We do not sell or rent your personal information. We only share it with the third-party providers
                listed above as needed to deliver our services, or when required by law.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-foreground mb-2">7. Your Rights</h2>
              <p>
                You may request to access, update, or delete the personal information we hold about you. To do so,
                contact us using the details below.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-foreground mb-2">8. Children's Privacy</h2>
              <p>
                Our services are not directed to children under the age of 13. We do not knowingly collect
                personal information from children.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-foreground mb-2">9. Your California Privacy Rights (CCPA/CPRA)</h2>
              <p className="mb-2">
                If you are a California resident, the California Consumer Privacy Act (CCPA), as amended by the
                California Privacy Rights Act (CPRA), gives you specific rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Right to know</strong> what personal information we collect, use, and disclose.</li>
                <li><strong>Right to delete</strong> personal information we have collected about you, subject to certain exceptions.</li>
                <li><strong>Right to correct</strong> inaccurate personal information we hold about you.</li>
                <li><strong>Right to opt out</strong> of the "sale" or "sharing" of personal information. We do not sell your personal information.</li>
                <li><strong>Right to limit</strong> the use of sensitive personal information.</li>
                <li><strong>Right to non-discrimination</strong> for exercising any of these rights.</li>
              </ul>
              <p className="mt-2">
                To exercise any of these rights, email us at <a href="mailto:clean@jeromecleaning.com" className="text-primary hover:underline">clean@jeromecleaning.com</a> or
                call (315) 308-1694. We will verify your identity before processing your request and will respond
                within 45 days as required by law. You may also designate an authorized agent to submit a request on your behalf.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-foreground mb-2">10. Your European Privacy Rights (GDPR)</h2>
              <p className="mb-2">
                If you are located in the European Economic Area (EEA), the United Kingdom, or Switzerland, the
                General Data Protection Regulation (GDPR) provides you with the following rights over your personal data:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Right of access</strong> — request a copy of the personal data we hold about you.</li>
                <li><strong>Right to rectification</strong> — ask us to correct inaccurate or incomplete data.</li>
                <li><strong>Right to erasure</strong> ("right to be forgotten") — request deletion of your data.</li>
                <li><strong>Right to restrict processing</strong> — ask us to pause how we use your data.</li>
                <li><strong>Right to data portability</strong> — receive your data in a machine-readable format.</li>
                <li><strong>Right to object</strong> to processing based on legitimate interests or for direct marketing.</li>
                <li><strong>Right to withdraw consent</strong> at any time, where processing is based on consent.</li>
                <li><strong>Right to lodge a complaint</strong> with your local data protection authority.</li>
              </ul>
              <p className="mt-2">
                <strong>Legal basis for processing:</strong> we rely on your consent (for marketing communications),
                contract performance (to deliver the cleaning services you book), our legitimate interests
                (to improve and secure our website), and legal obligations (such as tax and accounting requirements).
              </p>
              <p className="mt-2">
                <strong>International transfers:</strong> Jerome Cleaning is based in the United States. If you access
                our services from outside the U.S., your information will be transferred to and processed in the U.S.,
                which may have data-protection laws different from those in your country. We use appropriate
                safeguards (such as our providers' Standard Contractual Clauses) where required.
              </p>
              <p className="mt-2">
                To exercise any GDPR rights, contact us at <a href="mailto:clean@jeromecleaning.com" className="text-primary hover:underline">clean@jeromecleaning.com</a>.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-foreground mb-2">11. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. The "Last updated" date at the top of this
                page will reflect the most recent revision.
              </p>
            </section>

            <section>
              <h2 className="font-outfit text-xl font-semibold text-foreground mb-2">12. Contact Us</h2>
              <p className="mb-2">If you have any questions about this Privacy Policy or our data practices:</p>
              <div className="space-y-1">
                <p><strong>Jerome Cleaning</strong></p>
                <p>Syracuse, NY</p>
                <p>Email: <a href="mailto:clean@jeromecleaning.com" className="text-primary hover:underline">clean@jeromecleaning.com</a></p>
                <p>Phone: (315) 308-1694</p>
              </div>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Main App
function App() {
  return (
    <AuthProvider>
      <div className="App font-manrope">
        <Toaster position="top-right" />
        <BrowserRouter>
          <AnalyticsTracker />
          {/* SpecialOfferPopup disabled — HubSpot landing page (embedded on /) is now the primary conversion surface. */}
          {/* <SpecialOfferPopup /> */}
          <Navigation />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/quote" element={<QuotePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/book" element={
              <ProtectedRoute>
                <BookingPage />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <CustomerDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute adminOnly>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
          </Routes>
          <Footer />
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
